import { useRef } from 'react';
import { useFormikContext } from 'formik';
import { useBreakpoint } from '@app/hooks';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { Input, NumberInput } from 'cat-ecommerce-alloy';
import Field from '@app/components/common/form/Field';
import ItemRowDeleteButton from './ItemRowDeleteButton';
import { clearError } from '@app/store/exception/actions';
import { processPaste } from '@app/utils';
import { PAGE_BREAKPOINTS } from '@app/constants/commonConstants';
import { GENERAL } from '@app/constants/cookieContants';
import { DEFAULT_ROW_VALUES } from '@app/components/pages/checkout/shopping-cart/ShoppingCartPage/constants';
import styles from '../../ProductForm.module.scss';

const AddItemRow = ({
  addRow,
  deleteRow,
  errorInfo,
  isDefaultRow,
  isLast,
  itemNumberMaxLength = 20,
  itemNumberValidator,
  maxRows,
  numRows,
  quantityMaxLength = 5,
  quantityValidator,
  rowNum,
  showNotes,
  withCopyPastePopup,
  noteFieldsValidator
}) => {
  const [t] = useTranslation();
  const customerItemNumberMaxLength = 5;
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const { errors, setFieldValue, setFieldTouched, touched, values, isValid } =
    useFormikContext();

  const dispatch = useDispatch();
  const showNotesFields = [
    {
      title: 'assetId',
      csmask: true
    },
    {
      title: 'lineItemNote',
      csmask: false
    },
    {
      title: 'customerPartNumber',
      csmask: false
    },
    {
      title: 'customerItemNumber',
      csmask: false
    }
  ];

  const rowRef = useRef(null);
  const onInput = () => {
    if (errorInfo) {
      dispatch(clearError(errorInfo.domain, errorInfo.path));
    }
    if (isLast) {
      addRow(rowNum + 1);
    }
  };

  const onQuantityValidate = value => {
    if (quantityValidator) {
      return quantityValidator({
        value,
        setValue: setFieldValue,
        rowNum,
        touched,
        items: values.items,
        errorMessage: t('SKULIST_INVALID_QUANTITY')
      });
    }
  };

  const onItemNumberValidate = value => {
    if (itemNumberValidator) {
      return itemNumberValidator({
        value,
        setValue: setFieldValue,
        rowNum,
        touched,
        items: values.items
      });
    }
  };

  const onNoteFieldsValidate = (value, fieldName) => {
    if (noteFieldsValidator) {
      return noteFieldsValidator({
        value,
        setValue: setFieldValue,
        rowNum,
        touched,
        items: values.items,
        fieldName
      });
    }
  };

  const onItemNumberBlur = async e => {
    await setFieldValue(
      `items.${rowNum}.itemNumber`,
      e.target.value.trim(),
      false
    );
    setFieldValue(`items.${rowNum}.page`, GENERAL, false);
    await setFieldTouched(`items.${rowNum}.itemNumber`, true, true);
  };

  const onBlurInputChange = async (e, fieldName) => {
    await setFieldValue(
      `items.${rowNum}.${fieldName}`,
      e.target.value.trim(),
      false
    );
    await setFieldTouched(`items.${rowNum}.${fieldName}`, true, true);
  };

  const handlePaste = e => {
    if (!withCopyPastePopup) {
      return;
    }
    processPaste({
      addRow,
      defaultRowValues: DEFAULT_ROW_VALUES,
      dispatch,
      errorInfo,
      event: e,
      fileData: false,
      maxRows,
      numRows,
      rowNum,
      setFieldValue,
      values
    });
  };
  const getQuantityErrorText = () => {
    const item = values.items?.[rowNum] || {};
    const { itemNumber } = item;
    const rowTouched = touched.items?.[rowNum];
    const errorMessage = errors.items?.[rowNum]?.quantity;
    if (rowTouched && itemNumber && errorMessage) {
      return errors.items?.[rowNum]?.quantity;
    }
  };
  const getItemNumberErrorText = () => {
    const item = values.items?.[rowNum] || {};
    const { quantity, itemNumber } = item;
    const rowTouched = touched.items?.[rowNum];

    if (rowTouched && (itemNumber || quantity)) {
      return errors.items?.[rowNum]?.itemNumber;
    }
  };

  const getErrorTextByName = fieldName => {
    const fieldNameTitle = fieldName?.title;
    const item = values.items?.[rowNum] || {};
    const rowTouched = touched.items?.[rowNum];
    const fieldChanged = item[fieldNameTitle];

    if (rowTouched && fieldChanged) {
      return errors.items?.[rowNum]?.[fieldNameTitle];
    }
  };

  return (
    <div
      ref={rowRef}
      className={cx(
        'd-flex',
        'product-form-row',
        styles['product__form-row'],
        styles['product__form-error'],
        {
          'py-1': !isLast,
          'pb-0 pt-1': isLast,
          [`${styles['product__form-row-dynamic-add']}`]: !isDefaultRow
        }
      )}
    >
      <Field
        name={`items.${rowNum}.quantity`}
        onInput={onInput}
        type="text"
        as={NumberInput}
        style={{ width: '64px' }} //TODO: extract to form level class
        className="px-0 text-center"
        formGroupClassname="pe-1 my-0"
        getErrorText={getQuantityErrorText}
        validator={onQuantityValidate}
        onPaste={handlePaste}
        maxLength={quantityMaxLength}
      />
      <Field
        name={`items.${rowNum}.itemNumber`}
        onInput={onInput}
        type="text"
        as={Input}
        maxLength={itemNumberMaxLength}
        getErrorText={getItemNumberErrorText}
        formGroupClassname={cx('my-0', styles['product__form-column'], {
          'pe-1': showNotes
        })}
        onPaste={handlePaste}
        validator={onItemNumberValidate}
        onBlur={onItemNumberBlur}
      />
      {showNotes &&
        isMD &&
        showNotesFields.map((field, i) => (
          <Field
            key={field.title}
            name={`items.${rowNum}.${field.title}`}
            onInput={onInput}
            maxLength={
              field.title === 'customerItemNumber'
                ? customerItemNumberMaxLength
                : ''
            }
            type="text"
            as={Input}
            formGroupClassname={cx('my-0', styles['product__form-column'], {
              'pe-1': i !== 4
            })}
            onPaste={handlePaste}
            data-cs-mask={field.csmask}
            validator={value => onNoteFieldsValidate(value, field.title)}
            getErrorText={() => getErrorTextByName(field)}
            onBlur={e => onBlurInputChange(e, field)}
          />
        ))}
      <Field
        data-testid="pageNameQuickOrder"
        name={`items.${rowNum}.page`}
        type="text"
        as={Input}
        className={'d-none my-0'}
      />
      <ItemRowDeleteButton
        addRow={addRow}
        deleteRow={deleteRow}
        rowNum={rowNum}
        rowRef={rowRef}
      />
    </div>
  );
};

AddItemRow.propTypes = {
  addRow: PropTypes.func.isRequired,
  deleteRow: PropTypes.func.isRequired,
  errorInfo: PropTypes.shape({
    domain: PropTypes.string,
    message: PropTypes.string,
    path: PropTypes.string
  }),
  isDefaultRow: PropTypes.bool,
  isLast: PropTypes.bool,
  itemNumberMaxLength: PropTypes.number,
  itemNumberValidator: PropTypes.func,
  maxRows: PropTypes.number,
  numRows: PropTypes.number,
  quantityMaxLength: PropTypes.number,
  quantityValidator: PropTypes.func,
  rowNum: PropTypes.number.isRequired,
  showNotes: PropTypes.bool,
  withCopyPastePopup: PropTypes.bool,
  doubleByteValidator: PropTypes.func,
  noteFieldsValidator: PropTypes.func
};

export default AddItemRow;
